import { useControlled } from '@dni-react-hooks/use-controlled'
import { useMergeRefs } from '@dni-react-hooks/use-merge-refs'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useRef, type CSSProperties, type ChangeEvent, type ComponentPropsWithoutRef, type ForwardedRef, type ReactNode } from 'react'
import { Check, Minus } from '../Icons'
import { Text } from '../Text/Text'
import { colors } from '../tokens/colors.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const _iconStyles = stylex.create({
  root: {
    transition: 'opacity 200ms ease',
    opacity: 0,
  },
  show: {
    opacity: 1,
  },
})

const _checkboxStyles = stylex.create({
  root: {
    borderColor: colors.grey500,
    borderRadius: '2px',
    borderStyle: 'solid',
    borderWidth: '1px',
    alignItems: 'center',
    backgroundColor: colors.white,
    color: colors.white,
    display: 'flex',
    justifyContent: 'center',
    height: '16px',
    width: '16px',
  },
  checked: {
    borderColor: colors.grey600,
    backgroundColor: colors.grey600,
  },
  disabled: {
    borderColor: colors.grey400,
    backgroundColor: colors.grey100,
  },
  disableChecked: {
    borderColor: colors.grey400,
    backgroundColor: colors.grey400,
  },
})

const CustomCheckbox = stylex.create({
  root: (color: Color) => ({
    borderColor: colors[`${color}600`],
    backgroundColor: colors[`${color}600`],
  }),
})

const _baseStyles = stylex.create({
  root: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'inline-flex',

    // Reset for flastsome
    fontSize: 'inherit',
    justifyContent: 'center',
    marginBottom: 0,
  },
  disabled: {
    cursor: 'not-allowed',
  },
  input: {
    display: 'none',
  },
  label: {
    lineHeight: '160%',
    userSelect: 'none',
    marginLeft: '8px',
  },
  labelDisabled: {
    color: colors.grey400,
  },
})

type Color = 'primary' | 'grey' | 'blue'

export interface CheckboxProps extends Omit<ComponentPropsWithoutRef<'input'>, 'children'> {
  /**
   * input 元素的 ref
   */
  inputRef?: ForwardedRef<HTMLInputElement>
  /**
   * 根元素（label）的 ref
   */
  rootRef?: ForwardedRef<HTMLLabelElement>
  /**
   * 核取方塊顏色
   * @default 'primary'
   */
  color?: Color
  /**
   * 標籤文字顏色
   * @default 'secondary'
   */
  labelColor?: CSSProperties['color']
  /**
   * 標籤內容
   */
  label?: ReactNode
  /**
   * 根元素自訂樣式
   */
  rootStyle?: stylex.StyleXStyles
  /**
   * 是否為不確定狀態（半選）
   */
  indeterminate?: boolean
  /**
   * 覆寫內部樣式
   */
  xStyles?: {
    base?: Partial<XStyles<keyof typeof _baseStyles>>
    checkbox?: Partial<XStyles<keyof typeof _checkboxStyles>>
    icon?: Partial<XStyles<keyof typeof _iconStyles>>
  }
}

/**
 * Checkbox 用於讓使用者在多個選項中選取一個或多個。
 * 支援 checked / indeterminate 狀態與 label。
 *
 * @example
 * ```tsx
 * <Checkbox label="同意條款" checked={agreed} onChange={handleChange} />
 * ```
 */
export const Checkbox = (props: CheckboxProps) => {
  const {
    checked: checkedProps,
    disabled,
    indeterminate,
    inputRef,
    rootRef,
    onChange,
    label,
    rootStyle,
    xStyles,
    color = 'primary',
    labelColor = 'secondary',
    ...rest
  } = props
  const baseStyles = useXStyles(_baseStyles, xStyles?.base)
  const checkboxStyles = useXStyles(_checkboxStyles, xStyles?.checkbox)
  const iconStyles = useXStyles(_iconStyles, xStyles?.icon)

  const [checked, setChecked] = useControlled({
    controlled: checkedProps,
    default: false,
  })

  const internalInputRef = useRef<HTMLInputElement | null>(null)
  const setInputRef = useMergeRefs(internalInputRef, inputRef)

  useEffect(function syncIndeterminateToInput() {
    if (internalInputRef.current) {
      internalInputRef.current.indeterminate = !!indeterminate
    }
  }, [indeterminate])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setChecked(e.target.checked)
    onChange?.(e)
  }

  return (
    <label ref={rootRef} {...stylex.props(baseStyles.root, disabled && baseStyles.disabled, rootStyle)}>
      <input
        {...stylex.props(baseStyles.input)}
        checked={checked}
        type="checkbox"
        disabled={disabled}
        ref={setInputRef}
        onChange={handleChange}
        {...rest}
      />
      <div
        {...stylex.props(
          checkboxStyles.root,
          checked && checkboxStyles.checked,
          disabled && checkboxStyles.disabled,
          disabled && checked && checkboxStyles.disableChecked,
          indeterminate && checkboxStyles.checked,
          indeterminate && disabled && checkboxStyles.disableChecked,
          color && (checked || indeterminate) && CustomCheckbox.root(color),

        )}
      >
        {
          indeterminate
            ? (
              <Minus
                fontSize={16}
                rootStyle={[iconStyles.root, iconStyles.show]}
              />
            )
            : (
              <Check
                fontSize={16}
                rootStyle={[iconStyles.root, checked && iconStyles.show]}
              />
            )
        }
      </div>
      {label && (
        <Text
          rootStyle={[
            baseStyles.label,
            disabled && baseStyles.labelDisabled,
          ]}
          color={labelColor}
        >
          {label}
        </Text>

      )}
    </label>
  )
}
